const zitHelper = {
    hm: null,

    camel2dash: function (camel) {
        let dashed = camel.replace(/[A-Z]/g, m => "-" + m.toLowerCase());

        return dashed;
    },

    hasAttribute: function (element, attribute) {
        element = typeof element === 'string' ? document.querySelector(element) : element;
        if (element !== null) {
            let elementAttribute = this.camel2dash(attribute);
            if (typeof element.attributes[elementAttribute] !== typeof undefined) {
                return true;
            }
        }

        return false;
    },

    // Mirror one element-dataset entry onto another on the same element. Used by
    // theme behaviour adapters (e.g. metronic_8's mirrorMenuAttrs / imageInput /
    // passwordMeter) to bridge zit-namespaced markup to the framework-namespaced
    // attributes a third-party widget auto-discovers. Idempotent: only writes when
    // the destination key is currently unset, so an explicit value already present
    // on the destination is preserved (including an empty string). Silent no-op
    // when the source key is absent.
    //
    // Parameters:
    //   el             - the DOM element whose dataset is read/written.
    //   existingName   - source attribute name in camelCase form, prefix excluded
    //                    (e.g. 'menuTrigger' for data-zit-menu-trigger).
    //   newPrefix      - destination prefix segment (e.g. 'kt'); pass '' for none.
    //   existingPrefix - source prefix segment; defaults to 'zit' since every
    //                    current consumer mirrors zit-namespaced markup.
    //   newName        - destination attribute name; defaults to existingName so
    //                    the common "same suffix on both sides" case stays terse.
    mirrorDataAttribute: function (el, existingName, newPrefix, existingPrefix, newName) {
        // Default the source prefix to 'zit' for the common case.
        if (typeof existingPrefix === 'undefined') {
            existingPrefix = 'zit';
        }
        // Default the destination name to mirror the source name when not supplied.
        if (newName === null || typeof newName === 'undefined') {
            newName = existingName;
        }
        // Build the camelCased dataset keys. An empty prefix means the name stands
        // alone (data-foo -> dataset.foo); a non-empty prefix capitalises the first
        // letter of the name (data-zit-foo -> dataset.zitFoo).
        let existingKey = existingPrefix === '' ? existingName : existingPrefix + this.ucfirst(existingName);
        let newKey = newPrefix === '' ? newName : newPrefix + this.ucfirst(newName);
        // Only mirror when source exists and destination is unset — preserves any
        // existing destination value (including an explicit empty string).
        if (typeof el.dataset[existingKey] !== 'undefined' && typeof el.dataset[newKey] === 'undefined') {
            el.dataset[newKey] = el.dataset[existingKey];
        }
    },

    readAttribute: function (element, attribute) {
        let value = false;

        element = typeof element === 'string' ? document.querySelector(element) : element;
        if (element !== null) {
            let elementAttribute = this.camel2dash(attribute);
            if (typeof element.attributes[elementAttribute] !== typeof undefined) {
                return this.parseJson(element.attributes[elementAttribute].value);
            } else {
                this.toConsole('warn', 'attribute missing', attribute, element);
            }
        } else {
            this.toConsole('warn', 'no element found for query', element);
        }

        return value;
    },

    parseJson: function (string) {
        let value = string;
        try {
            value = JSON.parse(string);
        } catch (e) {
        }

        return value;
    },

    fetchJson: async function (url, options) {
        try {
            let response = await fetch(url, options);
            let jsonData = await response.json();
            return jsonData;
        } catch (e) {
            this.toConsole('warn', 'failed to load json', url, e);
            return false;
        }
    },

    // Text counterpart of fetchJson, deliberately shaped differently: fetchJson collapses
    // every failure into false, but an html consumer has to tell a 404 from a redirect to
    // the login page from a dead network. So any http status resolves with the status
    // reported, and false means only that fetch itself rejected (network / CORS block).
    fetchText: async function (url, options) {
        try {
            let response = await fetch(url, options);
            let text = await response.text();

            return {
                ok: response.ok,
                status: response.status,
                redirected: response.redirected,
                url: response.url,
                text: text
            };
        } catch (e) {
            this.toConsole('warn', 'failed to load html', url, e);
            return false;
        }
    },

    toConsole: function (messageType, ...variables) {
        let showGroup = variables.length > 1;

        if (showGroup) {
            let message = messageType;

            if (typeof variables[0] === 'string') {
                message = variables[0];
                variables = variables.slice(1);
            }

            console.group(message);
        }
        variables.forEach((variable) => {
            switch (messageType) {
                case 'log':
                    console.log(variable);
                    break;

                case 'warn':
                    console.warn(variable);
                    break;

                case 'error':
                    console.error(variable);
                    break;
            }
        });
        if (showGroup) {
            console.groupEnd();
        }
    },

    ucfirst: function (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    },

    lcfirst: function (string) {
        return string.charAt(0).toLowerCase() + string.slice(1);
    },

    showElement: function (element, show = true) {
        if (this.isString(element)) {
            element = document.getElementById(element);
        }

        if (show === 'toggle') {
            show = this.isHidden(element);
        }

        // Resolve the theme-mapped displayNone class via app.cssClass so the same
        // token is used everywhere (templates, JS toggles, PHP cssClassesMap).
        let displayNoneClass = this.hm.app.cssClass('displayNone');

        if (show) {
            element.classList.remove(displayNoneClass);
        } else {
            element.classList.add(displayNoneClass);
        }
    },

    hideElement: function (element) {
        this.showElement(element, false);
    },

    toggleVisibilityElement: function (element) {
        this.showElement(element, 'toggle');
    },

    isHidden: function (element) {
        if (typeof element.isHidden !== typeof undefined) {
            return element.isHidden;
        } else if (typeof element.element !== typeof undefined) {
            return this.isHidden(element.element);
        } else if (typeof element.classList !== typeof undefined) {
            // Match against the same theme-mapped class showElement/hideElement add/remove.
            return element.classList.contains(this.hm.app.cssClass('displayNone'));
        }

        return false;
    },

    isVisible: function (element) {
        return !this.isHidden(element);
    },

    getTemplate: function (template, parent) {
        if (typeof parent === typeof undefined) {
            parent = document;
        }

        let templateElement = parent.querySelector('template[data-zit-template=' + template + ']');
        return templateElement.innerHTML;
    },

    string2element: function (elementString) {
        let parser = new DOMParser();
        let doc = parser.parseFromString(elementString, 'text/html');

        return doc.body.firstChild;
    },

    inherit: function (from, to, properties = []) {
        for (let property in to) {
            if (properties.length === 0 || properties.includes(property)) {
                from[property] = to[property];
            }
        }
    },

    inheritAllBut: function (from, to, properties) {
        for (let property in to) {
            if (!properties.includes(property)) {
                from[property] = to[property];
            }
        }
    },

    isObject: function (obj) {
        return (!!obj) && (obj.constructor === Object);
    },

    isArray: function (arr) {
        return (!!arr) && (arr.constructor === Array);
    },

    isString: function (myVar) {
        return typeof myVar === 'string' || myVar instanceof String
    },

    objectPathValue: function (obj, path) {
        let pathArr = path.split('.');
        let currObj = obj;
        pathArr.forEach((parameter) => {
            if (currObj !== null && typeof currObj[parameter] !== typeof undefined) {
                currObj = currObj[parameter];
            } else {
                currObj = null;
            }
        });

        return currObj;
    },

    formPostData: function (formPostData, elementName, value) {
        let openingBracketPos = elementName.indexOf('[');

        if (openingBracketPos !== -1) {
            let closingBracketPos = elementName.indexOf(']');
            let actualElementName = elementName.substring(0, openingBracketPos);
            let key = elementName.substring(openingBracketPos + 1, closingBracketPos);
            if (key.length) {
                if (typeof formPostData[actualElementName] === typeof undefined) {
                    formPostData[actualElementName] = {};
                }

                formPostData[actualElementName][key] = value;
            } else {
                if (typeof formPostData[actualElementName] === typeof undefined) {
                    formPostData[actualElementName] = [];
                }

                formPostData[actualElementName].push(value);
            }
        } else {
            formPostData[elementName] = value;
        }

        return formPostData;
    },

    seconds2time: function (value, format) {
        let addZero = function (value) {
            let svalue = new String(value);

            if (svalue.length === 1) {
                svalue = '0' + svalue;
            }

            return svalue;
        };

        let tdate = new Date(1970, 0, 1, 0, 0, 0);
        tdate.setSeconds(value);

        let result = format;
        result = result.replace('HH', addZero(tdate.getHours()));
        result = result.replace('mm', addZero(tdate.getMinutes()));
        result = result.replace('ss', addZero(tdate.getSeconds()));

        return result;
    },

    getCookieValue: function (cookieName) {
        return document.cookie.match('(^|;)\\s*' + cookieName + '\\s*=\\s*([^;]+)')?.pop() || '';
    },

    datetimeString2Date: function (dateTimeStr) {
        let dateStr = dateTimeStr.substring(0, 10);
        let dateArr = dateStr.split('-');
        let timeStr = dateTimeStr.substring(11);
        let timeArr = timeStr.split(':');

        let dateObj = new Date(parseInt(dateArr[0]), parseInt(dateArr[1]) - 1, parseInt(dateArr[2]), timeArr[0], timeArr[1], timeArr[2]);

        return dateObj;
    },

    insecureHash: function (str) {
        let hash = 0;
        if (str.length === 0) return hash;
        for (let i = 0; i < str.length; i++) {
            const chr = str.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash & 0x7FFFFFFF;
    },

    arrayUnique: function (arr) {
        let unique = arr.filter((value, index, array) => array.indexOf(value) === index);

        return unique;
    },

    camelToKebab: function (str) {
        return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    }
};

module.exports = zitHelper;